import React from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import BrandMark from "@/components/BrandMark";
import {
  LayoutDashboard, Package, ShoppingBag, Users, FileText, Truck, Inbox,
  BadgeCheck, LogOut, Bell, Search, MessageSquare, ClipboardCheck, Building2, Home, TrendingUp, Gavel, UserCircle
} from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import LanguageSwitcher from "@/components/LanguageSwitcher";

function SideLink({ to, icon: Icon, label, testid }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
          isActive
            ? "bg-primary text-primary-foreground"
            : "text-secondary-foreground/80 hover:bg-white/5 hover:text-secondary-foreground"
        }`
      }
    >
      <Icon className="w-4 h-4 shrink-0" />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

export default function DashboardLayout({ children, title, actions }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const links = React.useMemo(() => {
    if (!user) return [];
    if (user.role === "admin")
      return [
        { to: "/dashboard", icon: LayoutDashboard, label: t("sidebar.overview"), testid: "sidebar-overview" },
        { to: "/admin/users", icon: Users, label: t("sidebar.users"), testid: "sidebar-users" },
        { to: "/admin/products", icon: Package, label: t("sidebar.products"), testid: "sidebar-products" },
        { to: "/admin/quotes", icon: FileText, label: t("sidebar.quotes"), testid: "sidebar-quotes" },
        { to: "/admin/inquiries", icon: Inbox, label: t("sidebar.inquiries"), testid: "sidebar-inquiries" },
        { to: "/admin/shipments", icon: Truck, label: t("sidebar.shipments"), testid: "sidebar-shipments" },
        { to: "/escrows", icon: ClipboardCheck, label: "Escrow", testid: "sidebar-escrows" },
        { to: "/buyer/samples", icon: Package, label: "Sample orders", testid: "sidebar-samples" },
        { to: "/admin/leads", icon: Building2, label: t("sidebar.leads"), testid: "sidebar-leads" },
        { to: "/admin/metrics", icon: TrendingUp, label: "MRR · GMV", testid: "sidebar-metrics" },
        { to: "/messages", icon: MessageSquare, label: t("sidebar.messages"), testid: "sidebar-messages" },
      ];
    if (user.role === "supplier")
      return [
        { to: "/dashboard", icon: LayoutDashboard, label: t("sidebar.overview"), testid: "sidebar-overview" },
        { to: "/supplier/profile", icon: UserCircle, label: "Company profile", testid: "sidebar-profile" },
        { to: "/supplier/products", icon: Package, label: t("sidebar.myProducts"), testid: "sidebar-products" },
        { to: "/supplier/rfq", icon: Gavel, label: t("sidebar.rfqBoard"), testid: "sidebar-rfq" },
        { to: "/supplier/reviews", icon: ClipboardCheck, label: "Reviews", testid: "sidebar-reviews" },
        { to: "/escrows", icon: FileText, label: "Escrow", testid: "sidebar-escrows" },
        { to: "/buyer/samples", icon: Package, label: "Sample orders", testid: "sidebar-samples" },
        { to: "/buyer/shipments", icon: Truck, label: t("sidebar.shipments"), testid: "sidebar-shipments" },
        { to: "/messages", icon: MessageSquare, label: t("sidebar.messages"), testid: "sidebar-messages" },
      ];
    return [
      { to: "/dashboard", icon: LayoutDashboard, label: t("sidebar.overview"), testid: "sidebar-overview" },
      { to: "/buyer/catalog", icon: ShoppingBag, label: t("sidebar.catalog"), testid: "sidebar-catalog" },
      { to: "/buyer/quotes", icon: FileText, label: t("sidebar.myQuotes"), testid: "sidebar-quotes" },
      { to: "/buyer/inquiries", icon: Inbox, label: t("sidebar.myInquiries"), testid: "sidebar-inquiries" },
      { to: "/escrows", icon: ClipboardCheck, label: "Escrow", testid: "sidebar-escrows" },
      { to: "/buyer/samples", icon: Package, label: "Sample orders", testid: "sidebar-samples" },
      { to: "/buyer/shipments", icon: Truck, label: t("sidebar.shipments"), testid: "sidebar-shipments" },
      { to: "/messages", icon: MessageSquare, label: t("sidebar.messages"), testid: "sidebar-messages" },
    ];
  }, [user, t]);

  const initials = (user?.name || user?.email || "?")
    .split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-72 flex-col bg-secondary text-secondary-foreground">
        <Link to="/" data-testid="sidebar-brand" className="px-6 py-5 flex items-center gap-3 border-b border-white/5">
          <BrandMark size={38} className="shrink-0" />
          <div className="leading-none">
            <div className="font-heading font-black tracking-tight text-white">
              Safeway<span className="text-primary">·</span>Finland
            </div>
            <div className="mt-1.5 text-[10px] font-heading font-black uppercase tracking-[0.18em] text-white/55">{t("sidebar.portal", { role: user?.role?.toUpperCase() })}</div>
          </div>
        </Link>
        <nav className="flex-1 px-3 py-6 space-y-1">
          {links.map((l) => <SideLink key={l.to} {...l} />)}
        </nav>
        <div className="px-3 pb-6 space-y-1">
          <SideLink to="/profile" icon={BadgeCheck} label={t("sidebar.profile")} testid="sidebar-profile" />
          <NavLink to="/" data-testid="sidebar-public" className="flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium text-secondary-foreground/70 hover:bg-white/5">
            <Home className="w-4 h-4" /> <span>{t("sidebar.publicSite")}</span>
          </NavLink>
          <button
            data-testid="sidebar-logout"
            onClick={async () => { await logout(); navigate("/"); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium text-secondary-foreground/80 hover:bg-white/5"
          >
            <LogOut className="w-4 h-4" /> {t("sidebar.signOut")}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 h-16 bg-white/90 backdrop-blur-xl border-b border-border flex items-center justify-between px-6">
          <div className="flex items-center gap-3 min-w-0">
            <h1 data-testid="page-title" className="text-lg font-heading font-semibold tracking-tight text-secondary truncate">{title}</h1>
          </div>
          <div className="flex items-center gap-2">
            {actions}
            <LanguageSwitcher />
            <NotificationBell />
            <div className="hidden sm:flex items-center gap-3 pl-3 border-l border-border">
              <Avatar className="w-8 h-8">
                <AvatarFallback className="bg-secondary text-primary text-xs font-bold">{initials}</AvatarFallback>
              </Avatar>
              <div className="leading-tight">
                <div data-testid="user-name" className="text-sm font-medium text-secondary">{user?.name}</div>
                <div className="text-xs text-muted-foreground capitalize">{user?.role}</div>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 p-6 lg:p-10 max-w-[1400px] w-full">{children}</main>
      </div>
    </div>
  );
}
